import React, { useState, useEffect, useCallback, useRef } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useApi } from '../../hooks/useApi';
import { getApiBase } from '../../config/config';
import { useAuth } from '../../context/AuthContext';
import Loader from '../common/Loader';
import './DemoVideos.css';

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const DemoVideos = () => {
  const { apiCall } = useApi();
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [videos, setVideos] = useState([]);
  const [uploadModal, setUploadModal] = useState(null); // { file, description }
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewVideo, setPreviewVideo] = useState(null); // { id, filename, url }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, filename }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [renameModal, setRenameModal] = useState(null); // { id, description }
  const [renameLoading, setRenameLoading] = useState(false);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/admin/demo-videos');
      setVideos(data.videos || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [apiCall]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleFilePick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.type !== 'video/mp4') {
      setError('Only MP4 files are allowed.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File exceeds the ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB limit.`);
      return;
    }

    setError('');
    setUploadModal({ file, description: '' });
  };

  const handleUploadConfirm = async () => {
    if (!uploadModal) return;
    const { file, description } = uploadModal;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    if (description.trim()) {
      formData.append('description', description.trim());
    }

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${getApiBase()}/admin/demo-videos`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            let detail = `Upload failed with status ${xhr.status}`;
            try {
              const body = JSON.parse(xhr.responseText);
              if (body.detail) detail = body.detail;
            } catch {}
            reject(new Error(detail));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(formData);
      });
      setUploadModal(null);
      await fetchVideos();
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handlePreview = async (video) => {
    setError('');
    try {
      const data = await apiCall(`/admin/demo-videos/${video.id}/url`);
      setPreviewVideo({ ...video, url: data.url });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyUrl = async (video) => {
    setError('');
    try {
      const data = await apiCall(`/admin/demo-videos/${video.id}/url`);
      await navigator.clipboard.writeText(data.url);
      setCopiedId(video.id);
      setTimeout(() => setCopiedId((c) => (c === video.id ? null : c)), 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      await apiCall(`/admin/demo-videos/${deleteConfirm.id}`, { method: 'DELETE' });
      setVideos((prev) => prev.filter((v) => v.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
      setDeleteConfirm(null);
    }
    setDeleteLoading(false);
  };

  const handleRename = async () => {
    if (!renameModal) return;
    setRenameLoading(true);
    try {
      const updated = await apiCall(`/admin/demo-videos/${renameModal.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ description: renameModal.description }),
      });
      setVideos((prev) => prev.map((v) => (v.id === updated.id ? { ...v, description: updated.description } : v)));
      setRenameModal(null);
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
      setRenameModal(null);
    }
    setRenameLoading(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="dv-page">
      <div className="page-header">
        <h2 className="page-title">Demo Videos ({videos.length})</h2>
        <div className="dv-header-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={handleFilePick}
            disabled={uploading}
          >
            <CloudUploadIcon sx={{ fontSize: '1rem' }} />
            {uploading ? `Uploading... ${uploadProgress}%` : 'Upload MP4'}
          </button>
          <button onClick={fetchVideos} className="btn-icon" disabled={loading || uploading}>
            <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Upload Modal */}
      {uploadModal && (
        <div className="modal-overlay" onClick={() => !uploading && setUploadModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Upload Demo Video</h3>
              <button onClick={() => setUploadModal(null)} className="btn-close" disabled={uploading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">File</label>
                <p className="dv-upload-filename">{uploadModal.file.name} ({formatBytes(uploadModal.file.size)})</p>
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter a description..."
                  value={uploadModal.description}
                  onChange={(e) => setUploadModal({ ...uploadModal, description: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !uploading) handleUploadConfirm(); }}
                  disabled={uploading}
                  autoFocus
                />
              </div>
              {uploading && (
                <div className="dv-progress-wrap" style={{ marginTop: '0.75rem' }}>
                  <div className="dv-progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setUploadModal(null)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUploadConfirm}
                disabled={uploading}
              >
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Description</th>
                <th>Size</th>
                <th>Uploaded By</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-results">No demo videos uploaded yet</td>
                </tr>
              ) : (
                videos.map((v) => (
                  <tr key={v.id} className="user-row">
                    <td title={v.filename}>{v.filename}</td>
                    <td title={v.description}>{v.description || <span className="text-muted">—</span>}</td>
                    <td>{formatBytes(v.size)}</td>
                    <td className="td-id" title={v.uploaded_by}>{v.uploaded_by || '—'}</td>
                    <td className="td-date">{formatDate(v.updated)}</td>
                    <td className="td-actions">
                      <button
                        className="btn-icon-sm"
                        title="Preview"
                        onClick={() => handlePreview(v)}
                      >
                        <PlayArrowIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm"
                        title={copiedId === v.id ? 'Copied!' : 'Copy signed URL'}
                        onClick={() => handleCopyUrl(v)}
                      >
                        <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm"
                        title="Edit description"
                        onClick={() => setRenameModal({ id: v.id, description: v.description || '' })}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </button>
                      <button
                        className="btn-icon-sm btn-icon-danger"
                        title="Delete"
                        onClick={() => setDeleteConfirm({ id: v.id, filename: v.filename })}
                      >
                        <DeleteIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {previewVideo && (
        <div className="modal-overlay" onClick={() => setPreviewVideo(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{previewVideo.filename}</h3>
              <button onClick={() => setPreviewVideo(null)} className="btn-close">
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <video
                key={previewVideo.url}
                src={previewVideo.url}
                controls
                autoPlay
                className="dv-preview-video"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Delete <strong className="user-id-highlight">{deleteConfirm.filename}</strong>?
              </p>
              <p className="confirm-subtext">
                This will permanently remove the file from the bucket. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Rename / Edit Description Modal */}
      {renameModal && (
        <div className="modal-overlay" onClick={() => !renameLoading && setRenameModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Description</h3>
              <button onClick={() => setRenameModal(null)} className="btn-close" disabled={renameLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={renameModal.description}
                  onChange={(e) => setRenameModal({ ...renameModal, description: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setRenameModal(null)}
                disabled={renameLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRename}
                disabled={renameLoading || !renameModal.description.trim()}
              >
                {renameLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoVideos;
